import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHomeData } from '@/hooks/useTranslatedData';

export default function AboutSection() {
  const { t } = useTranslation('common');
  const aboutData = useHomeData().about;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-brand-cream py-16 md:py-24">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left: Content */}
          <div>
            <span
              className={`inline-block px-4 py-1.5 bg-brand-gray-pale/30 rounded-md text-xs font-semibold text-brand-gray tracking-wide mb-6 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {aboutData.label}
            </span>

            <h2
              className={`text-4xl md:text-5xl lg:text-6xl font-bold text-brand-charcoal leading-tight mb-6 transition-all duration-700 delay-150 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <span className="block">{aboutData.title}</span>
              <span className="block text-brand-green">{aboutData.titleLine2}</span>
            </h2>

            <p
              className={`text-sm md:text-base text-brand-gray leading-relaxed whitespace-pre-line mb-10 transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {aboutData.description}
            </p>

            {/* Stats */}
            <div
              className={`grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 transition-all duration-700 delay-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center sm:text-left">
                  <span className="block text-2xl md:text-3xl font-bold text-brand-charcoal mb-1">
                    {stat.number}
                  </span>
                  <span className="text-xs text-brand-gray-light">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Manifesto Card */}
          <div
            className={`transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-brand-green rounded-2xl p-8 md:p-10 relative overflow-hidden">
              {/* Decorative dots */}
              <div className="absolute top-6 right-6 flex gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
              </div>

              <span className="block text-xs text-white/50 uppercase tracking-wider mb-6">
                {aboutData.manifesto.label}
              </span>

              <p className="text-base md:text-lg text-white leading-relaxed mb-10">
                {aboutData.manifesto.text}
              </p>

              {/* Bottom info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                    <i className="ri-flask-line text-white text-sm"></i>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-white">
                      {aboutData.manifesto.founderName}
                    </span>
                    <span className="block text-xs text-white/50">
                      {aboutData.manifesto.founderRole}
                    </span>
                  </div>
                </div>

                <Link
                  to="/about"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors whitespace-nowrap"
                >
                  {t('misc.learnMore')}
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>

            {/* Decorative image beside card */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20scientific%20laboratory%20equipment%20testing%20natural%20food%20ingredients%2C%20microscope%20slide%20with%20plant%20cells%2C%20warm%20amber%20lighting%2C%20clean%20modern%20lab%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20editorial%20science%20photography%20style&width=300&height=200&seq=40&orientation=landscape"
                alt={t('misc.learnMore')}
                className="w-full h-32 md:h-40 object-cover rounded-xl"
                loading="lazy"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20modern%20food%20laboratory%20interior%20with%20white%20surfaces%20and%20glass%20equipment%2C%20natural%20botanical%20ingredients%20on%20workbench%2C%20soft%20warm%20lighting%2C%20minimal%20scientific%20aesthetic%2C%20green%20and%20white%20color%20palette&width=300&height=200&seq=41&orientation=landscape"
                alt={t('misc.learnMore')}
                className="w-full h-32 md:h-40 object-cover rounded-xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}